import { IUndoRedoService } from "../../../../platform/undo/common/undo.js";
import { IKeyboardService } from "../../../../platform/keyboard/common/keyboardService.js";
import { IContextService } from "../../../../platform/context/common/contextService.js";
import { IKeybindingService } from "../../../../platform/keybinding/common/keybindingService.js";
import { createDecorator, registerSingleton } from "../../../../platform/instantiation/common/registry.js";
import { SyncDescriptor } from "../../../../platform/instantiation/common/descriptors.js";
import { Disposable, toDisposable } from "../../../../platform/../base/common/lifecycle.js";
import { IIdentitiyService } from "../../../../platform/auth/common/auth.js";
import { ILicenseService } from "../../../../platform/auth/common/licenses.js";
import { IFileService } from "../../../../platform/files/common/files.js";
import { IInstantiationService } from "../../../../platform/instantiation/common/instantiation.js";
import { IAIService } from "../../../../platform/ai/common/ai.js";
import { ISecretStorageService } from "../../../../platform/secrets/common/secrets.js";
import { IWebGLService } from "../../../../platform/webgl/common/webgl.js";
import { IProjectService } from "../../project/common/project.js";
import { ILogService } from "../../../../platform/log/common/log.js";
import { IUserConfiguration } from "../../user-configuration/common/userConfiguration.js";
import { BUILD_HASH, BUILD_TIME, BUILD_ENVIRONMENT, isNeedleEditorHTMLElement, BUILD_SEMVER, IS_EMBEDDED_INSPECTOR } from "../../../../base/common/needle.js";
import { shouldPassManagedInput } from "./editorInputCapture.js";
import { IAnalyticsService } from "../../../../platform/analytics/common/analytics.js";
import { viewportTelemetryProps } from "../../../../platform/analytics/common/viewport.js";
import { IFileDialogService } from "../../../../platform/dialogs/common/dialogs.js";
import { CommandsRegistry, ICommandService } from "../../../../platform/commands/common/commands.js";
import { MenuId, MenuRegistry } from "../../../../platform/actions/common/actions.js";
import { MenubarControl } from "../../../services/titlebar/menubarControl.js";
import { localize } from "../../../../base/common/nls.js";
import { IHierarchyService } from "../../../../platform/hierarchy/common/hierarchy.js";
import { IPropertyService } from "../../../../platform/properties/common/properties.js";
import { INodeGraphInspectionService } from "../../../../platform/webgl/three.js/nodeGraphInspectionService.js";
import { IMeshBakeGroupService } from "../../meshBaker/common/bakeGroups.js";
import { ICodeViewService } from "../../../../platform/code/common/codeView.js";
import "../../../../platform/code/common/codeViewService.js"; // registers CodeViewService (registerSingleton side effect)
import "../../../../platform/properties/common/starredProperties.js"; // registers StarredPropertyService (registerSingleton side effect)
import { Emitter, Event } from "../../../../base/common/events.js";
import { ContextKeyExpr } from "../../../../platform/contextkey/common/contextkey.js";
import { developmentUrlKind, stripQueryString } from "../../../../base/browser/url.js";
import { needleEngineTelemetryProps } from "../../../../base/common/needleEngineRuntime.js";
import { IProductService } from "../../../../platform/product/common/productService.js";
import { IQuickInputService } from "../../../../platform/quickinput/common/quickInput.js";
import { registerQuickInputKeybindings } from "../../../../platform/quickinput/common/quickInputKeybindings.js";
import { INavigationDeviceService } from "../../../../platform/navigation/common/navigationDevice.js";
import { INotificationService } from "../../../../platform/notifications/common/notifications.js";
import { IWindowsMainService } from "../../../../platform/windows/common/windows.js";
import { monitorInspectorAccess, reportInspectorAccessFailure, shouldMonitorInspectorAccess } from "../../../../platform/auth/common/inspectorAccess.js";


// #region Register Menu Items
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    title: "Create",
    group: "1_create",
    order: 0,
    submenu: MenuId.ExplorerContextCreateSubmenu,
    icon: {
        id: "file"
    }
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    title: "Copy",
    group: "2_copy",
    order: 2,
    submenu: MenuId.ExplorerContextCopySubmenu,
    icon: {
        id: "copy"
    }
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    title: "Export",
    group: "3_export",
    order: 3,
    submenu: MenuId.ExplorerContextExportSubmenu,
    icon: {
        id: "export"
    }
});
MenuRegistry.appendMenuItem(MenuId.ExplorerContext, {
    title: "Experimental",
    group: "4_experimental",
    order: 4,
    submenu: MenuId.ExplorerContextExperimentalSubmenu,
    icon: {
        id: "experimental"
    }
});


// #region Application
export interface IApplication {

    readonly onDidChange: Event<{ active?: boolean }>;

    get isActive(): boolean;

    readonly mainInstantiationService: IInstantiationService;
    readonly windowsMainService: IWindowsMainService;

    readonly productService: IProductService;
    readonly undo: IUndoRedoService;
    readonly logger: ILogService;
    readonly keyboard: IKeyboardService;
    readonly fileservice: IFileService;
    readonly commands: ICommandService;
    readonly context: IContextService;
    readonly keybindings: IKeybindingService;
    readonly identitiy: IIdentitiyService | null;
    readonly licenses: ILicenseService | null;
    readonly secretStorage: ISecretStorageService;
    readonly ai: IAIService;
    readonly webgl: IWebGLService | null;
    readonly userConfiguration: IUserConfiguration;
    readonly project: IProjectService | null;
    readonly fileDialogService: IFileDialogService;
    readonly hierarchyService: IHierarchyService | null;
    readonly propertyService: IPropertyService | null;
    readonly nodeGraphInspection: INodeGraphInspectionService | null;
    readonly meshBakeGroups: IMeshBakeGroupService | null;
    readonly codeView: ICodeViewService | null;
    readonly menubarControl: MenubarControl;
}

interface ApplicationChangedEvent {
    /** true when active state changed */
    active?: boolean;
}

export class Application extends Disposable implements IApplication {

    private readonly _onDidChange = this._register(new Emitter<ApplicationChangedEvent>());
    readonly onDidChange = this._onDidChange.event;

    private _isActive: boolean = true;
    get isActive(): boolean {
        return this._isActive;
    }
    set isActive(value: boolean) {
        if (this._isActive !== value) {
            this._isActive = value;
            // Active indicates that 'active' state has changed
            this._onDidChange.fire({ active: true });
        }
    }

    constructor(
        active: boolean = true,
        @IInstantiationService readonly mainInstantiationService: IInstantiationService,
        @IWindowsMainService readonly windowsMainService: IWindowsMainService,
        @IProductService readonly productService: IProductService,
        @IUndoRedoService readonly undo: IUndoRedoService,
        @ILogService readonly logger: ILogService,
        @IKeyboardService readonly keyboard: IKeyboardService,
        @IFileService readonly fileservice: IFileService,
        @ICommandService readonly commands: ICommandService,
        @IContextService readonly context: IContextService,
        @IKeybindingService readonly keybindings: IKeybindingService,
        @IIdentitiyService readonly identitiy: IIdentitiyService | null,
        @ILicenseService readonly licenses: ILicenseService | null,
        @ISecretStorageService readonly secretStorage: ISecretStorageService,
        @IAIService readonly ai: IAIService,
        @IWebGLService readonly webgl: IWebGLService | null,
        @IProjectService readonly project: IProjectService,
        @IUserConfiguration readonly userConfiguration: IUserConfiguration,
        @IAnalyticsService readonly analytics: IAnalyticsService | null,
        @IFileDialogService readonly fileDialogService: IFileDialogService,
        @IHierarchyService readonly hierarchyService: IHierarchyService | null,
        @IPropertyService readonly propertyService: IPropertyService | null,
        @INodeGraphInspectionService readonly nodeGraphInspection: INodeGraphInspectionService | null,
        @IMeshBakeGroupService readonly meshBakeGroups: IMeshBakeGroupService | null,
        @ICodeViewService readonly codeView: ICodeViewService | null,
    ) {
        super();
        this._isActive = active;

        this.registerApplicationCommands();
        this.registerEventListeners();
        this.logger.debug("Application initialized.");
        this.menubarControl = this._register(this.mainInstantiationService.createInstance(new SyncDescriptor(MenubarControl)));

        // Detection for chrome when the editor becomes active by selecting a node
        // Only checking for nodes added is also triggered by e.g. the webxr extension
        if (this.hierarchyService) {
            Event.once(this.hierarchyService.onNodeAdded)(_evt => {
                const off = this.hierarchyService?.onSelectedNodesDidChange(evt => {
                    if (evt.selection.length > 0) {
                        this.isActive = true;
                        off?.dispose();
                        // async: resolve the pro-license flag first (see method)
                        void this.reportActivationTelemetry();
                    }
                });
            });
        }

        this.mainInstantiationService.invokeFunction(accessor => {

            const quickinput = accessor.getIfExists(IQuickInputService);
            const navigationDevices = accessor.getIfExists(INavigationDeviceService);
            const notifications = accessor.getIfExists(INotificationService);
            if (this.licenses && notifications && shouldMonitorInspectorAccess()) {
                this._register(monitorInspectorAccess(
                    this.licenses,
                    notifications,
                    error => reportInspectorAccessFailure(error, this.logger, this.analytics),
                ));
            }
            if (quickinput) {
                this._register(registerQuickInputKeybindings(this.keybindings));
            }

            if (navigationDevices) {
                this._register(CommandsRegistry.registerCommand({
                    id: "camera.input.toggleKeyboardMouse",
                    label: "Toggle Camera Keyboard/Mouse Input",
                    execute: () => {
                        this.userConfiguration.cameraKeyboardMouseEnabled = !this.userConfiguration.cameraKeyboardMouseEnabled;
                        this.logger.info(`Camera keyboard/mouse input ${this.userConfiguration.cameraKeyboardMouseEnabled ? "enabled" : "disabled"}.`);
                    }
                }));
                this._register(CommandsRegistry.registerCommand({
                    id: "camera.input.toggleKeyboardMouseCapture",
                    label: "Toggle Camera Keyboard/Mouse Capture",
                    execute: () => {
                        this.userConfiguration.cameraKeyboardMouseCapture = !this.userConfiguration.cameraKeyboardMouseCapture;
                        this.logger.info(`Camera keyboard/mouse capture ${this.userConfiguration.cameraKeyboardMouseCapture ? "enabled" : "disabled"}.`);
                    }
                }));
                this._register(CommandsRegistry.registerCommand({
                    id: "camera.input.toggleGamepad",
                    label: "Toggle Camera Gamepad Input",
                    execute: () => {
                        this.userConfiguration.cameraGamepadEnabled = !this.userConfiguration.cameraGamepadEnabled;
                        this.logger.info(`Camera gamepad input ${this.userConfiguration.cameraGamepadEnabled ? "enabled" : "disabled"}.`);
                    }
                }));
                this._register(CommandsRegistry.registerCommand({
                    id: "camera.input.toggleSpaceMouse",
                    label: "Toggle Camera SpaceMouse Input",
                    execute: async () => {
                        const enable = !this.userConfiguration.cameraSpaceMouseEnabled;
                        if (enable) {
                            const connected = await this.ensureSpaceMouseReady(navigationDevices, notifications, false);
                            if (!connected) {
                                return;
                            }
                        }
                        this.userConfiguration.cameraSpaceMouseEnabled = enable;
                        this.logger.info(`Camera SpaceMouse input ${this.userConfiguration.cameraSpaceMouseEnabled ? "enabled" : "disabled"}.`);
                    }
                }));
                this._register(CommandsRegistry.registerCommand({
                    id: "camera.input.connectSpaceMouse",
                    label: "Connect SpaceMouse",
                    metadata: { description: "Connect a SpaceMouse and enable camera input from it", palette: true },
                    execute: async () => {
                        const connected = await this.ensureSpaceMouseReady(navigationDevices, notifications, true);
                        if (connected) {
                            this.userConfiguration.cameraSpaceMouseEnabled = true;
                            notifications?.success("SpaceMouse connected and camera input enabled.", { duration: 2500 });
                        }
                    }
                }));
                this._register(CommandsRegistry.registerCommand({
                    id: "camera.smoothing.increase",
                    label: "Increase Camera Smoothing",
                    execute: () => {
                        this.userConfiguration.cameraSmoothing = roundCameraSmoothing(this.userConfiguration.cameraSmoothing + .1);
                        this.logger.info(`Camera smoothing increased to ${this.userConfiguration.cameraSmoothing.toFixed(1)}.`);
                    }
                }));
                this._register(CommandsRegistry.registerCommand({
                    id: "camera.smoothing.decrease",
                    label: "Decrease Camera Smoothing",
                    execute: () => {
                        this.userConfiguration.cameraSmoothing = roundCameraSmoothing(this.userConfiguration.cameraSmoothing - .1);
                        this.logger.info(`Camera smoothing decreased to ${this.userConfiguration.cameraSmoothing.toFixed(1)}.`);
                    }
                }));
                this._register(CommandsRegistry.registerCommand({
                    id: "camera.smoothing.reset",
                    label: "Reset Camera Smoothing",
                    execute: () => {
                        this.userConfiguration.cameraSmoothing = .5;
                        this.logger.info("Camera smoothing reset to defaults.");
                    }
                }));
            }

        })
    }

    readonly menubarControl: MenubarControl;

    /**
     * "Editor became active" telemetry (fired once, on the first node selection),
     * tagged with whether the user holds a PRO/inspector license.
     *
     * The license loads ASYNCHRONOUSLY — the identity service restores the session
     * and the license service fetches on login — so we resolve it BEFORE logging.
     * `hasInspectorLicense()` triggers/awaits that fetch when logged in (coalescing
     * an in-flight refresh) and resolves false when logged out. Bounded by a short
     * timeout that falls back to the last-known sync value, so a slow/hung license
     * fetch never delays or drops the event. `pro` is left undefined if there's no
     * license service (agent/headless).
     */
    private async reportActivationTelemetry(): Promise<void> {
        let pro: boolean | undefined = undefined;
        const licenses = this.licenses;
        if (licenses) {
            try {
                pro = await Promise.race([
                    licenses.hasInspectorLicense(),
                    new Promise<boolean>(resolve => setTimeout(() => resolve(licenses.isInspectorLicensed === true), 4000)),
                ]);
            }
            catch { /* never block telemetry on the license — leave undefined */ }
        }
        // window size (the host page's viewport the inspector runs in) at activation
        const viewport = viewportTelemetryProps(typeof window !== "undefined" ? window : undefined);
        this.analytics?.logEvent("pageview", { reason: "node_selected", build: BUILD_ENVIRONMENT, hash: BUILD_HASH, time: BUILD_TIME, version: BUILD_SEMVER, pro, ...viewport });
        const devKind = developmentUrlKind(window.location.href);
        if (devKind) {
            // dev_context: "network" (localhost/LAN), "cloud" (AI-builder previews,
            // cloud IDEs) or "file" (a build opened straight off disk).
            // The url goes through stripQueryString for the same reason the
            // pageview event carries no querystring: "cloud" covers Codespaces,
            // Gitpod, Replit and the AI-builder previews, whose urls routinely
            // carry forwarded-port auth and signed preview params. The fragment
            // stays — that's the hash route, which is the useful half.
            this.analytics?.logEvent("local-dev", { url: stripQueryString(window.location.href), dev_context: devKind, build: BUILD_ENVIRONMENT, hash: BUILD_HASH, time: BUILD_TIME, version: BUILD_SEMVER, pro, ...needleEngineTelemetryProps() });
        }
    }

    private registerApplicationCommands(): void {
        // Register undo command
        this._register(CommandsRegistry.registerCommand({
            id: 'edit.undo',
            label: 'Undo',
            metadata: { description: 'Undo the last action', palette: true },
            canExecute: () => ({
                canExecute: this.undo.canUndo
            }),
            execute: () => this.undo.undo()
        }));
        this.keybindings.registerKeybinding({
            id: "keybinding.undo",
            key: "Ctrl+Z",
            commandId: "edit.undo",
            // chrome ext: undo after a gizmo drag, while the host canvas still
            // has focus — canExecute (canUndo) keeps Ctrl+Z with the host page
            // until the editor actually has something to undo
            allowOnCanvas: true,
        });
        // Register redo command
        this._register(CommandsRegistry.registerCommand({
            id: 'edit.redo',
            label: 'Redo',
            metadata: { description: 'Redo the last undone action', palette: true },
            canExecute: () => ({
                canExecute: this.undo.canRedo
            }),
            execute: () => this.undo.redo()
        }));
        this.keybindings.registerKeybinding({
            id: "keybinding.redo",
            key: "Ctrl+Y",
            commandId: "edit.redo",
            allowOnCanvas: true,
        });
        this.keybindings.registerKeybinding({
            id: "keybinding.redo-alt",
            key: "Ctrl+Shift+Z",
            commandId: "edit.redo",
            allowOnCanvas: true,
        });
        // Delete / Ctrl+D act on the SELECTION, so an object picked in the 3D
        // viewport works too — the tree's focused-row handling alone left them
        // dead after a viewport click. allowOnCanvas + the commands' canExecute
        // (selection required, duplicate must exist in this product) keep the
        // keys with the host page whenever the editor cannot use them.
        this.keybindings.registerKeybinding({
            id: "keybinding.deleteSelection",
            key: ["Delete", "Backspace"],
            commandId: "hierarchy.deleteSelected",
            when: "!timelineItemSelected",
            allowOnCanvas: true,
            preventDefault: true,
        });
        this.keybindings.registerKeybinding({
            id: "keybinding.duplicateSelection",
            key: "Ctrl+D",
            commandId: "hierarchy.duplicateSelected",
            allowOnCanvas: true,
            // the service's typing guard only covers modifier-LESS keys, and
            // Ctrl+D belongs to the code editor while you are writing in it
            when: "!timelineItemSelected && !textInputFocus",
            // the browser's bookmark dialog must not ALSO open
            preventDefault: true,
        });
        // Clear undo history: drops (and disposes) all undo/redo entries — also
        // releases objects retained for undo (e.g. deleted nodes and their
        // geometry/materials), making them garbage-collectable.
        this._register(CommandsRegistry.registerCommand({
            id: 'edit.clearUndo',
            label: 'Clear Undo History',
            metadata: { description: 'Drop all undo/redo entries (also releases objects retained for undo)', palette: true },
            canExecute: () => ({
                canExecute: this.undo.canUndo || this.undo.canRedo
            }),
            execute: () => this.undo.clear()
        }));

        // Undo redo menu items
        MenuRegistry.appendMenuItem(MenuId.MenubarEditMenu, {
            command: {
                id: "edit.undo",
                title: 'Undo',
                icon: { id: "undo" },
                precondition: ContextKeyExpr.has("undoRedo.hasUndo"),
            },
            title: {
                value: 'Undo',
                original: 'Undo',
                mnemonicTitle: localize({ key: 'mUndo', comment: ['&& denotes a mnemonic'] }, "&&Undo"),
            },
            order: 0,
            group: 'undo'
        });
        MenuRegistry.appendMenuItem(MenuId.MenubarEditMenu, {
            command: {
                id: "edit.redo",
                title: 'Redo',
                icon: { id: "redo" },
                precondition: ContextKeyExpr.has("undoRedo.hasRedo"),
            },
            title: {
                value: 'Redo',
                original: 'Redo',
                mnemonicTitle: localize({ key: 'mRedo', comment: ['&& denotes a mnemonic'] }, "&&Redo"),
            },
            order: 1,
            group: 'undo'
        });
        MenuRegistry.appendMenuItem(MenuId.MenubarEditMenu, {
            command: {
                id: "edit.clearUndo",
                title: 'Clear Undo History',
                icon: { id: "delete" },
                precondition: ContextKeyExpr.or(ContextKeyExpr.has("undoRedo.hasUndo"), ContextKeyExpr.has("undoRedo.hasRedo")),
            },
            title: {
                value: 'Clear Undo History',
                original: 'Clear Undo History',
            },
            order: 2,
            group: 'undo'
        });

        MenuRegistry.appendMenuItem(MenuId.MenubarPreferencesMenu, {
            command: {
                id: "camera.input.connectSpaceMouse",
                title: "Connect SpaceMouse",
                icon: { id: "gamepad" },
            },
            title: {
                value: "Connect SpaceMouse",
                original: "Connect SpaceMouse",
            },
            order: 20,
            group: "input"
        });
        MenuRegistry.appendMenuItem(MenuId.MenubarPreferencesMenu, {
            command: {
                id: "camera.input.toggleSpaceMouse",
                title: "Toggle SpaceMouse Camera Input",
                icon: { id: "gamepad" },
            },
            title: {
                value: "Toggle SpaceMouse Camera Input",
                original: "Toggle SpaceMouse Camera Input",
            },
            order: 21,
            group: "input"
        });


        if (IS_EMBEDDED_INSPECTOR) {
            this._register(CommandsRegistry.registerCommand({
                id: 'ui.toggleLayout',
                label: 'Toggle UI Layout',
                execute: () => {
                    switch (this.userConfiguration.uiLayout) {
                        case "minimal":
                            this.userConfiguration.uiLayout = "default";
                            break;
                        case "default":
                        default:
                            this.userConfiguration.uiLayout = "minimal";
                            break;
                    }
                }
            }));
            this._register(this.keybindings.registerKeybinding({
                id: "keybinding.toggleUI",
                key: "Ctrl+U",
                commandId: "ui.toggleLayout",
            }));
        }
    }


    private registerEventListeners(): void {

        // Wheel ownership belongs to fly controls through IPointerService; blocking
        // it here can prevent delivery when host DOM covers the render canvas.
        const inputEventTypes = [
            'keydown',
            'keyup',
            'keypress',
            'mousedown',
            'mouseup',
            'click',
            'dblclick',
            'touchstart',
            'touchend',
            'touchmove',
        ];

        for (const eventType of inputEventTypes) {
            window.addEventListener(eventType, this.onInputEvent, {
                capture: true,
                passive: false
            });
            this._register(toDisposable(() => {
                window.removeEventListener(eventType, this.onInputEvent, {
                    capture: true,
                });
            }));
        }
    }


    private onInputEvent = (event: globalThis.Event) => {
        // Only REAL user input is isolated. A synthetic event was dispatched by
        // code — often our own — and preventDefault-ing it cancels the very thing
        // that dispatched it. Concretely: exporting builds an <a download> and
        // calls .click(); that anchor sits in the page body, not in the editor UI,
        // so the "outside the editor" branch below preventDefault-ed it — and the
        // default action of clicking a download link IS the download. Result: with
        // the fly camera active, export ran, reported success, and no file ever
        // appeared.
        if (!event.isTrusted) return;
        // Prevent input events when editor camera is active and the event target is outside of the needle editor UI
        // This is a workaround to prevent unwanted interactions with other UI elements when using the editor camera
        const editorCameraIsActive = this.webgl?.getAllEngines().some(e => e.editorCamera);
        const shouldCaptureKeyboardMouse = this.userConfiguration.cameraKeyboardMouseEnabled && this.userConfiguration.cameraKeyboardMouseCapture;
        if (editorCameraIsActive && shouldCaptureKeyboardMouse) {

            const keyboardEvent = event.type === "keydown" ? event as KeyboardEvent : undefined;
            const binding = keyboardEvent ? this.keybindings.resolveKeybinding(keyboardEvent) : undefined;
            const commandCanExecute = binding
                ? this.commands.canExecuteCommand(binding.commandId, binding.args)?.canExecute !== false
                : false;
            if (shouldPassManagedInput(event.type, binding, commandCanExecute)) return true;

            if (event.type === "keydown") {
                const keyboardEvent = event as KeyboardEvent;
                // Allow modifier keys to propagate
                if (keyboardEvent.key === "Shift" || keyboardEvent.key === "Control" || keyboardEvent.key === "Alt" || keyboardEvent.key === "Meta") {
                    this.logger.debug(`Allowed modifier key event '${event.type}'`);
                    return true;
                }
                // Allow F1-F12 keys to propagate
                if (keyboardEvent.key.startsWith("F") && !isNaN(parseInt(keyboardEvent.key.substring(1)))) {
                    this.logger.debug(`Allowed function key event '${event.type}'`);
                    return true;
                }
                if (this.keyboard.isKeyPressed("Control") || this.keyboard.isKeyPressed("Meta")) {
                    // Allow key combinations with Control or Meta (Cmd) to propagate
                    this.logger.debug(`Allowed key combination event '${event.type}' with Control/Meta`);
                    return true;
                }
            }

            if (!isNeedleEditorHTMLElement(event.target)) {
                // is up event
                if (event.type === 'keyup' || event.type === 'pointerup' || event.type === 'mouseup' || event.type === 'touchend') {
                    // Let up events pass through to avoid stuck keys/buttons
                    this.logger.trace(`Allowed input event '${event.type}' to avoid stuck keys/buttons.`);
                    return true;
                }

                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                this.logger.trace(`Blocked input event '${event.type}' outside of needle editor UI.`, event.target);
                return false;
            }
            else {
                // Events ON our own UI: hands off. This listener is on `window` in
                // the CAPTURE phase, so a stopPropagation() here does not just
                // shield the host page — it stops the event from ever DESCENDING
                // to our own components. That silently killed wheel interactions
                // inside the editor whenever the fly camera was active (texture /
                // resource preview zoom, EnumProperty scrubbing).
                // Keeping the host page from also reacting is the event-isolation
                // layer's job (capture-phase listeners ON the editor elements —
                // see core.ui/docs/EVENT_ISOLATION.md), not this one's.
                this.logger.trace(`Allowed input event '${event.type}' on needle editor UI.`);
                return true;
            }
        }
    }

    private async ensureSpaceMouseReady(navigationDevices: INavigationDeviceService, notifications: INotificationService | null | undefined, explicitConnect: boolean): Promise<boolean> {
        const connected = await navigationDevices.requestSpaceMouseDevice();
        if (connected) {
            return true;
        }

        if (explicitConnect) {
            notifications?.warn("No SpaceMouse device was selected.", { duration: 3000 });
        }
        else {
            notifications?.warn("Select a SpaceMouse device to enable camera input.", { duration: 3000 });
        }
        this.userConfiguration.cameraSpaceMouseEnabled = false;
        return false;
    }


}

function roundCameraSmoothing(value: number): number {
    const normalized = Math.max(0, Math.min(1, value));
    return Math.round(normalized * 10) / 10;
}

export const IApplication = createDecorator<IApplication>('application');

registerSingleton(IApplication, new SyncDescriptor(Application));
